import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Modal from '../components/Modal';
import SkiAreaSelectModal from '../components/SkiAreaSelectModal';
import { fetchScenarios, createScenario, deleteScenario } from '../services/lvcService';
import { useError } from '../contexts/ErrorContext';
import './LVCPage.css';

const LVCPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();
  const [searchQuery, setSearchQuery] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSkiAreaModal, setShowSkiAreaModal] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [createError, setCreateError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [openScenarioMenu, setOpenScenarioMenu] = useState(null); // Which scenario's menu is open

  const loadScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchScenarios();
      setScenarios(data);
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      const errorMsg = error.message || 'Failed to load scenarios';
      addError(errorMsg, error.stack, 'LVC Page');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const handleCreateNew = () => {
    setShowCreateModal(true);
    setNewScenarioName('');
    setCreateError(null);
  };

  const handleNameSubmit = () => {
    if (!newScenarioName.trim()) {
      setCreateError('Scenario name is required');
      return;
    }
    // Close name modal and open ski area selection modal
    setShowCreateModal(false);
    setShowSkiAreaModal(true);
    setCreateError(null);
  };

  const handleSkiAreaSelect = async (skiArea) => {
    // skiArea is null for "Unlisted", otherwise it's the ski area object
    const skiAreaId = skiArea ? skiArea.id : null;
    setShowSkiAreaModal(false);

    // Now create the scenario with the selected ski area
    try {
      setIsCreating(true);
      setCreateError(null);
      const scenario = await createScenario(newScenarioName.trim(), skiAreaId);
      setNewScenarioName('');
      // Navigate to the new scenario
      navigate(`/lvc/${scenario.id}`);
    } catch (error) {
      setCreateError(error.message || 'Failed to create scenario');
      // Reopen the name modal if creation fails
      setShowCreateModal(true);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateScenario = async () => {
    // This is now just a wrapper that opens ski area selection
    handleNameSubmit();
  };

  const handleScenarioClick = (scenarioId) => {
    navigate(`/lvc/${scenarioId}`);
  };

  const handleDeleteScenario = async (scenarioId, e) => {
    e.stopPropagation();
    setOpenScenarioMenu(null);
    
    if (!window.confirm(`Are you sure you want to delete "${scenarioId}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await deleteScenario(scenarioId);
      // Reload scenarios after deletion
      loadScenarios();
    } catch (error) {
      addError(error.message || 'Failed to delete scenario', error.stack, 'LVC Page');
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openScenarioMenu !== null) {
        const menuContainer = event.target.closest('.scenario-menu-container');
        if (!menuContainer) {
          setOpenScenarioMenu(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openScenarioMenu]);

  // Filter scenarios based on search query
  const filteredScenarios = scenarios.filter(scenario =>
    scenario.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="lvc-list-page">
      <header className="lvc-list-header">
        <div className="lvc-list-header-content">
          <h1>Live Virtual Constructive (LVC)</h1>
          <Button
            variant="primary"
            size="medium"
            onClick={handleCreateNew}
          >
            Create New Scenario
          </Button>
        </div>
      </header>
      <main className="lvc-list-main">
        <div className="lvc-list-content">
          <div className="lvc-search-section">
            <input
              type="text"
              className="lvc-search-input"
              placeholder="Search scenarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="lvc-scenarios-list">
            {loading ? (
              <div className="empty-scenarios">
                <p>Loading scenarios...</p>
              </div>
            ) : filteredScenarios.length === 0 ? (
              <div className="empty-scenarios">
                <p>{searchQuery ? 'No scenarios match your search.' : 'No scenarios found.'}</p>
                <p className="empty-hint">Click "Create New Scenario" to get started.</p>
              </div>
            ) : (
              <div className="scenarios-grid">
                {filteredScenarios.map((scenario) => {
                  const isMenuOpen = openScenarioMenu === scenario.id;
                  return (
                    <div
                      key={scenario.id}
                      className="scenario-card"
                      onClick={() => handleScenarioClick(scenario.id)}
                    >
                      <div className="scenario-card-header">
                        <h3>{scenario.name}</h3>
                        <div className="scenario-menu-container">
                          <button
                            className="scenario-menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenScenarioMenu(isMenuOpen ? null : scenario.id);
                            }}
                          >
                            ⋯
                          </button>
                          {isMenuOpen && (
                            <div className="scenario-menu-dropdown">
                              <button
                                className="scenario-menu-item danger"
                                onClick={(e) => handleDeleteScenario(scenario.id, e)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="scenario-card-body">
                        <p className="scenario-date">Created: {scenario.date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewScenarioName('');
          setCreateError(null);
        }}
        title="Create New Scenario"
      >
        <div className="create-scenario-form">
          <label htmlFor="scenario-name">Scenario Name</label>
          <input
            id="scenario-name"
            type="text"
            className="scenario-name-input"
            placeholder="Enter scenario name..."
            value={newScenarioName}
            onChange={(e) => {
              setNewScenarioName(e.target.value);
              setCreateError(null);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isCreating) {
                handleCreateScenario();
              }
            }}
            autoFocus
          />
          {createError && (
            <p className="create-error">{createError}</p>
          )}
          <div className="modal-actions">
            <Button
              variant="primary"
              onClick={handleCreateScenario}
              disabled={isCreating || !newScenarioName.trim()}
            >
              Next
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewScenarioName('');
                setCreateError(null);
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <SkiAreaSelectModal
        isOpen={showSkiAreaModal}
        onClose={() => {
          setShowSkiAreaModal(false);
          // Reopen name modal if user cancels ski area selection
          setShowCreateModal(true);
        }}
        onSelect={handleSkiAreaSelect}
      />
    </div>
  );
};

export default LVCPage;
